import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';

import request, { apiHost } from '../utils/request';
import { SalesManagerForm, SalesSupportForm } from './forms';
import { RegisterAsSalesRepAndManager, RegisterAsSalesSupport } from './schema';
import Header from './header';
import { CONSTANT } from '../utils/constant';
import { TranslationsContext } from '../../context/translations';
import ValidateLoader from '../UI/validateLoader';
import UserRole from './forms/fields/userRole';

const fetchChannels = () => {
  return request.get('/channels');
};

const fetchProvinces = () => {
  return request.get('/provinces');
};

const fetchDealerships = () => {
  return request.get('/dealerships');
};

const Registration = ({ history }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [userRole, setUserRole] = useState('2');
  const [showModal, setShowModal] = useState(false);
  const [isError, setError] = useState(null);
  const [recaptcha, setRecaptcha] = useState(null);
  const [channels, setChannels] = useState(null);
  const [provinces, setProvinces] = useState(null);
  const [dealerships, setDealerships] = useState(null);
  const translations = useContext(TranslationsContext);

  const [processingQueryData, setProcessingQueryData] = useState(true);
  const [isRedirectedFromPassport, setIsRedirectedFromPassport] = useState(
    false
  );
  const [paramsData, setParamsData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });

  const {
    USERS: {
      USER_AUTHENTICATION: { labels, confirmationDialog }
    }
  } = translations;

  const userRoles = Object.keys(CONSTANT.ROLES).map(role => ({
    id: CONSTANT.ROLES[role],
    name: labels[role]
  }));

  const isRoleSalesSupport = userRole === '5';

  useEffect(() => {
    setIsLoading(true);
    Promise.allSettled([
      fetchChannels(),
      fetchProvinces(),
      fetchDealerships()
    ]).then(([channelsResp, provincesResp, dealershipsResp]) => {
      const {
        value: {
          data: { channels: channelsData }
        }
      } = channelsResp;
      const {
        value: {
          data: { provinces: provincesData }
        }
      } = provincesResp;
      const {
        value: {
          data: { dealerships: dealershipsData }
        }
      } = dealershipsResp;

      setChannels(channelsData);
      setProvinces(provincesData);
      setDealerships(dealershipsData);
      setIsLoading(false);
    });
  }, []);

  // extract and store data from query params (if any)
  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    const firstName = decodeURIComponent(queryParams.get('firstName') || '');
    const lastName = decodeURIComponent(queryParams.get('lastName') || '');
    const email = decodeURIComponent(queryParams.get('email') || '');

    if (firstName && lastName && email) setIsRedirectedFromPassport(true);

    setParamsData({
      firstName: firstName || '',
      lastName: lastName || '',
      email: email || ''
    });

    setProcessingQueryData(false);
  }, []);

  const onCaptchaChange = value => {
    setRecaptcha(value);
  };

  const handleSubmit = (values, actions) => {
    setError(null);
    setIsSaving(true);

    const updatedValues = {
      ...values,
      recaptcha,
      role: userRole,
      channel: values.channel.map(i => (typeof i === 'object' ? i.id : i))
    };

    axios
      .post(`${apiHost}/users/register`, updatedValues)
      .then(response => {
        if (response.status === 200) {
          setShowModal(true);
        }
        setIsSaving(false);
        actions.resetForm({});
      })
      .catch(err => {
        const {
          data: { error }
        } = err && err.response;

        setShowModal(false);
        setIsSaving(false);
        setError(error);
      });
  };

  return isLoading || processingQueryData ? (
    <ValidateLoader message={translations.general.loading} />
  ) : (
    <div id="main" style={{ marginTop: '54px', paddingBottom: '93px' }}>
      <div id="main-wrapper">
        <div id="main-content">
          <Header error={isError} />
          <div id="main-content-login">
            <UserRole
              roles={userRoles}
              labels={labels}
              setUserRole={setUserRole}
            />
            {[2, 3].includes(+userRole) && (
              <SalesManagerForm
                initialValues={{
                  firstName: paramsData.firstName,
                  lastName: paramsData.lastName,
                  email: paramsData.email,
                  password: '',
                  channel: [],
                  outlet: '',
                  outletCode: '',
                  xid: '',
                  salesManager: '',
                  salesManagerEmail: '',
                  channelOrgCode: '',
                  salesRepId: '',
                  contactNumber: '',
                  dealership: [],
                  province: '',
                  partnerId: ''
                }}
                schema={RegisterAsSalesRepAndManager}
                labels={labels}
                showModal={showModal}
                loading={isSaving}
                onCaptchaChange={onCaptchaChange}
                channels={channels}
                provinces={provinces}
                dealerships={dealerships}
                confirmationDialog={confirmationDialog}
                onSubmit={handleSubmit}
                isRedirectedFromPassport={isRedirectedFromPassport}
                userRole={+userRole}
              />
            )}
            {isRoleSalesSupport && (
              <SalesSupportForm
                initialValues={{
                  firstName: paramsData.firstName,
                  lastName: paramsData.lastName,
                  email: paramsData.email,
                  password: '',
                  xid: '',
                  contactNumber: '',
                  dealership: [],
                  channelOrgCode: '',
                  province: [],
                  channel: []
                }}
                schema={RegisterAsSalesSupport}
                onSubmit={handleSubmit}
                confirmationDialog={confirmationDialog}
                labels={labels}
                provinces={provinces}
                channels={channels}
                showModal={showModal}
                loading={isSaving}
                onCaptchaChange={onCaptchaChange}
                dealerships={dealerships}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

Registration.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    location: PropTypes.shape({
      search: PropTypes.string.isRequired
    }).isRequired
  }).isRequired
};

export default Registration;
